import { useLocation } from 'react-router-dom';
import { useAccountContext } from '../auth/useAccountContext';

/**
 * BT001 #16306 — the honesty notice for screens the switcher does not yet reach.
 *
 * The switcher is global, but only Departments and Domains are adopted in this
 * Story. The screens below have no account-scoped endpoint to point at yet —
 * verified: every action on ClientItQuotesController scopes to profile.AccountId
 * and its own XML doc states an ?accountId query parameter is not accepted.
 *
 * Without this notice a partner admin who switches to a child and lands on one of
 * those screens would see the PARENT's data under a CHILD's label — a correctness
 * defect worse than today's behaviour. Each screen removes itself from this list
 * in Bug #16303 as its scoped endpoint lands.
 */
export const NOT_YET_ACCOUNT_SCOPED_PATHS = [
  '/invoices',
  '/m365-users',
  '/security-posture',
  '/onboarding',
  '/onboarding/welcome',
  '/it-admin/quotes',
  // #20979 — Work orders: GET /client-it/work-orders scopes to profile.AccountId and takes no accountId.
  '/work-orders',
  // #20977 — Change approvals: GET /client-it/change-requests scopes to profile.AccountId and takes no accountId.
  '/change-approvals',
];

/**
 * Path PREFIXES of not-yet-account-scoped detail screens, whose paths carry an id and so cannot be listed exactly.
 * #10279 — /work-orders/:id: GET /client-it/work-orders/{id} also scopes to profile.AccountId.
 */
export const NOT_YET_ACCOUNT_SCOPED_PREFIXES = [
  '/work-orders/',
];

/** True when the screen at this path still shows the caller's own account whatever the switcher says. */
export function isNotYetAccountScoped(pathname: string): boolean {
  return NOT_YET_ACCOUNT_SCOPED_PATHS.includes(pathname)
    || NOT_YET_ACCOUNT_SCOPED_PREFIXES.some((prefix) => pathname.startsWith(prefix) && pathname.length > prefix.length);
}

export function OwnAccountOnlyNotice() {
  const { isParent, activeAccountId, ownAccountId, ownAccountName } = useAccountContext();
  const { pathname } = useLocation();

  if (!isParent) return null;
  if (!activeAccountId || !ownAccountId) return null;
  // On the caller's own account nothing is misleading — the screen is already correct.
  if (activeAccountId === ownAccountId) return null;
  if (!isNotYetAccountScoped(pathname)) return null;

  return (
    <div
      role="status"
      className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      <p className="font-medium">This screen still shows your own account</p>
      <p className="mt-1">
        Account switching does not reach this page yet, so the data below belongs to{' '}
        <span className="font-medium">{ownAccountName || 'your own account'}</span> — not the account
        selected in the top bar.
      </p>
    </div>
  );
}
